import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  inject,
  OnInit,
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  Data,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import Waves, { init } from 'node-waves';
import { Title } from '@angular/platform-browser';
import { filter, map, merge, startWith } from 'rxjs';
import { UiLocaleService } from '@core/services/ui-locale.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { AuthService } from '@core/services/auth.service';
import { GoogleAnalyticsService } from '@core/services/google-analytics.service';
import { AnalyticsConsent } from './components/analytics-consent/analytics-consent';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AnalyticsConsent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements AfterViewInit, OnInit {
  private readonly contentScrollHostSelector =
    '.app-body, .app-content, .content-wrapper, .content-wrapper-right, .main-content';
  private titleService = inject(Title);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private viewportScroller = inject(ViewportScroller);
  private authService = inject(AuthService);
  private readonly analytics = inject(GoogleAnalyticsService);
  private uiLocaleService = inject(UiLocaleService);
  private routeTranslations = inject(UiTranslationService).namespace('routes');

  ngAfterViewInit() {
    Waves.attach('.waves-effect', ['waves-light']);
    Waves.init();
  }

  ngOnInit(): void {
    this.analytics.initialize();
    if (this.authService.isLoggedIn()) {
      this.uiLocaleService
        .syncFromUserPreference()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }

    const navigationEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    );
    const localeChanged$ = toObservable(this.uiLocaleService.locale, {
      injector: this.injector,
    });

    merge(navigationEnd$, localeChanged$)
      .pipe(
        startWith(null),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route.snapshot.data;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => this.updateDocumentTitle(data));

    navigationEnd$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.analytics.trackPage();
        this.resetScrollAfterNavigation();
      });
  }

  private updateDocumentTitle(data: Data): void {
    const title = this.resolveTitle(data);
    if (!title) {
      return;
    }

    this.titleService.setTitle(`${title} | Reazly`);
  }

  private resolveTitle(data: Data): string {
    const titleKey = data['titleKey'];
    if (typeof titleKey === 'string' && titleKey.length > 0) {
      const translated = this.routeTranslations.tr(titleKey);
      if (translated !== titleKey) {
        return translated;
      }
    }

    const fallbackTitle = data['title'];
    return typeof fallbackTitle === 'string' ? fallbackTitle : '';
  }

  private resetScrollAfterNavigation(): void {
    this.resetScrollToTop();

    if (typeof globalThis.requestAnimationFrame !== 'function') {
      return;
    }

    globalThis.requestAnimationFrame(() => this.resetScrollToTop());
  }

  private resetScrollToTop(): void {
    // Router scrolling handles the main viewport; this is a fallback for
    // layout containers that can become scroll hosts in this template.
    this.viewportScroller.scrollToPosition([0, 0]);

    const documentRef = this.getDocument();
    if (!documentRef) {
      return;
    }

    const viewportHosts = [
      documentRef.scrollingElement,
      documentRef.documentElement,
      documentRef.body,
    ].filter(
      (element): element is HTMLElement => element instanceof HTMLElement,
    );

    [...new Set(viewportHosts)].forEach((element) =>
      this.scrollElementToTop(element),
    );

    const contentScrollHosts = documentRef.querySelectorAll<HTMLElement>(
      this.contentScrollHostSelector,
    );

    contentScrollHosts.forEach((element) => this.scrollElementToTop(element));
  }

  private getDocument(): Document | null {
    return typeof document === 'undefined' ? null : document;
  }

  private scrollElementToTop(element: HTMLElement): void {
    if (element.scrollTop <= 0 && element.scrollLeft <= 0) {
      return;
    }

    if (typeof element.scrollTo === 'function') {
      element.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    element.scrollTop = 0;
    element.scrollLeft = 0;
  }
}
